import type { AiOperationsCurrentProposal, ApprovalProposalDetail } from "@adpilot/shared";
import { IconArrowRight, IconChecklist } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import { RiskLegendTip } from "@/components/shared/RiskLegendTip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatSignedCurrencyDiff, useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ActionColumn, ActionGridRow, DecisionBadge } from "./DecisionRecordPrimitives";
import { riskTextClass, targetTitle, targetTypeLabel } from "./decision-record-presentation";

export function AiSummary({
  onOpenAnalysis,
  proposal,
}: {
  onOpenAnalysis: () => void;
  proposal: AiOperationsCurrentProposal;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <section className="flex min-h-[84px] items-center justify-between rounded-[10px] border border-line bg-surface px-[18px] py-4">
      <div className="flex max-w-[770px] flex-col gap-[5px]">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium leading-4 text-muted-foreground">
            {t("runDetail.decisionRecord.summary.label")}
          </span>
          <Badge variant="neutral">
            {t(
              `runDetail.decisionRecord.summary.health.${proposal.artifact.analysis.campaignHealth}`,
            )}
          </Badge>
        </div>
        <p className="text-base font-semibold leading-[23px] text-ink">
          {proposal.artifact.analysis.summary}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {/* 与「查看完整分析」打开同一弹窗,仅作"里面有 N 条结论"的提示;
            0 条时无内容可看,隐藏(ADP-127 反馈)。 */}
        {proposal.artifact.conclusions.length > 0 ? (
          <ControlButton controlSize="toolbar" variant="outline" onClick={onOpenAnalysis}>
            {t("runDetail.decisionRecord.summary.observations", {
              count: proposal.artifact.conclusions.length,
            })}
          </ControlButton>
        ) : null}
        <ControlButton controlSize="toolbar" onClick={onOpenAnalysis}>
          {t("runDetail.decisionRecord.summary.fullAnalysis")}
        </ControlButton>
      </div>
    </section>
  );
}

export function ActionReviewTable({
  canDecide,
  decidedCount,
  decisionTotal,
  pendingCount,
  onOpenAction,
  onReviewAll,
  proposal,
  review,
}: {
  canDecide: boolean;
  decidedCount: number;
  decisionTotal: number;
  pendingCount: number;
  onOpenAction: (actionId: string) => void;
  onReviewAll: () => void;
  proposal: AiOperationsCurrentProposal;
  review?: ApprovalProposalDetail;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const progress = decisionTotal > 0 ? Math.min(100, (decidedCount / decisionTotal) * 100) : 100;
  return (
    <section className="overflow-hidden rounded-[10px] border border-line bg-surface">
      <div className="flex min-h-[67px] items-center justify-between border-b border-line px-4 py-3.5">
        <div>
          <h2 className="text-base font-semibold leading-5 text-ink">
            {t("runDetail.decisionRecord.actions.title")}
          </h2>
          <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
            {t("runDetail.decisionRecord.actions.description")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs leading-4 text-muted-foreground">
            {t("runDetail.decisionRecord.actions.progress", {
              decided: decidedCount,
              total: decisionTotal,
            })}
          </span>
          <span className="h-[5px] w-[84px] overflow-hidden rounded-full bg-control-hover">
            <span
              className="block h-full rounded-full bg-amber transition-[width] duration-200"
              style={{ width: `${progress}%` }}
            />
          </span>
          {canDecide && pendingCount > 1 ? (
            <ControlButton controlSize="toolbar" onClick={onReviewAll}>
              <IconChecklist data-icon="inline-start" />
              {t("runDetail.decisionRecord.actions.reviewAll", { count: pendingCount })}
            </ControlButton>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[1080px]">
          <ActionGridRow header>
            <ActionColumn>{t("runDetail.decisionRecord.actions.columns.target")}</ActionColumn>
            <ActionColumn>{t("runDetail.decisionRecord.actions.columns.bidChange")}</ActionColumn>
            <ActionColumn>{t("runDetail.decisionRecord.actions.columns.aiReason")}</ActionColumn>
            <ActionColumn className="flex items-center gap-1">
              {t("runDetail.decisionRecord.actions.columns.guardrail")}
              <RiskLegendTip
                thresholds={proposal.artifact.validation.effectivePolicy.bidChangeRiskThresholds}
              />
            </ActionColumn>
            <ActionColumn className="text-right">
              {t("runDetail.decisionRecord.actions.columns.decision")}
            </ActionColumn>
          </ActionGridRow>
          {proposal.artifact.actions.map((action, index) => (
            <ActionRow
              key={action.id}
              action={action}
              canDecide={canDecide}
              review={review}
              shaded={index % 2 === 1}
              onOpen={() => onOpenAction(action.id)}
            />
          ))}
        </div>
      </div>

      <div className="flex min-h-[45px] items-center border-t border-line bg-table-footer px-4 py-3">
        <p className="text-xs leading-4 text-muted-foreground">
          {canDecide
            ? t("runDetail.decisionRecord.actions.decideInline")
            : t("runDetail.decisionRecord.actions.readOnly")}
        </p>
      </div>
    </section>
  );
}

function ActionRow({
  action,
  canDecide,
  onOpen,
  review,
  shaded,
}: {
  action: AiOperationsCurrentProposal["artifact"]["actions"][number];
  canDecide: boolean;
  onOpen: () => void;
  review?: ApprovalProposalDetail;
  shaded: boolean;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { formatCurrency } = useFormatters();
  const reviewAction = review?.actions.find(({ id }) => id === action.id);
  const target = review?.targets.find(({ id }) => id === action.target.id);
  const persistedDecision = reviewAction?.reviewStatus;
  const increase = action.server.changePercentage >= 0;
  const isFinal = persistedDecision && persistedDecision !== "pending";
  return (
    <ActionGridRow className={cn(shaded && "bg-table-footer")}>
      <ActionColumn>
        <p className="truncate text-[13px] font-semibold leading-[18px] text-ink">
          {targetTitle(action, t)}
        </p>
        <p className="mt-[3px] truncate text-xs leading-4 text-muted-foreground">
          {targetTypeLabel(action, target?.keywordMatchType, t)}
        </p>
      </ActionColumn>
      <ActionColumn>
        <p className="text-[13px] font-semibold leading-[18px] text-ink">
          {formatCurrency(action.server.oldBid)} → {formatCurrency(action.server.newBid)}
        </p>
        <p className={cn("mt-0.5 text-xs leading-4", increase ? "text-green-11" : "text-red-11")}>
          {formatSignedCurrencyDiff(action.server.oldBid, action.server.newBid, formatCurrency)}
        </p>
      </ActionColumn>
      <ActionColumn>
        <p className="whitespace-pre-wrap break-words text-xs leading-[18px] text-ink">
          {action.agent.reasoning}
        </p>
      </ActionColumn>
      <ActionColumn>
        <p
          className={cn("text-xs font-semibold leading-4", riskTextClass(action.server.riskLevel))}
        >
          {t(`runDetail.decisionRecord.risk.${action.server.riskLevel}`)}
        </p>
      </ActionColumn>
      <ActionColumn className="flex justify-end">
        {isFinal ? (
          <Button variant="ghost" className="h-auto border-0 p-0" onClick={onOpen}>
            <DecisionBadge status={persistedDecision} />
          </Button>
        ) : (
          <ControlButton controlSize="compact" variant="outline" onClick={onOpen}>
            {canDecide
              ? t("runDetail.decisionRecord.actions.reviewAction")
              : t("runDetail.decisionRecord.actions.viewAction")}
            <IconArrowRight data-icon="inline-end" />
          </ControlButton>
        )}
      </ActionColumn>
    </ActionGridRow>
  );
}
